/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect, useRef } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

/*============================================================
  MATERIAL UI
============================================================*/
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { Button } from "@mui/material";
import Grid from "@mui/material/Grid2";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridDropdown from "../../../widgets/GridDropdown";
import GridTextfield from "../../../widgets/GridTextfield";
import GridCheckbox from "../../../widgets/GridCheckbox";
import { CatchingPokemonSharp, ConstructionOutlined } from "@mui/icons-material";

import Multiplier from "./Multiplier.js";
import Rider from "./Rider.js";

/*============================================================
  PROPRIETARY - REDUX DATA SLICES
============================================================*/
import { updateProductRecommendation } from "../../../../states/slcProductRecommendation.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import { reNumbers } from "../../../../utils/fieldValidator.js";
import { createPayload, createReduxAction } from "../../../../utils/modelHelper.js";
import { validateCurrencyField3 } from "../../../../utils/fieldValidator.js";

const needsMap = new Map();
needsMap.set("familyAndAssets", "Family And Assets");
needsMap.set("disability", "Disability");
needsMap.set("criticalIlness", "Critical Illness");
needsMap.set("longTermCare", "Long Term Care");
needsMap.set("hospitalization", "Hospitalization");
needsMap.set("personalAccident", "Personal Accident");
needsMap.set("pregnancy", "Pregnancy");
needsMap.set("dependantNeeds", "Dependant Needs");
needsMap.set("retirementPlanning", "Retirement Planning");
needsMap.set("childrenEducation", "Children Education");
needsMap.set("otherPurpose", "Other Purpose");

const companiesMap = new Map();
var productCategoryMap = new Map();
var productMap = new Map();
var riderMap = new Map();

const paymentFrequencyData = [
  { key: "Annual", value: "Annual" },
  { key: "Half-Yearly", value: "Half-Yearly" },
  { key: "Quarterly", value: "Quarterly" },
  { key: "Monthly", value: "Monthly" },
  { key: "Single", value: "Single" },
];

const settlementModeData = [
  { key: "Cash", value: "Cash" },
  { key: "CPF-OA", value: "CPF-OA" },
  { key: "CPF-SA", value: "CPF-SA" },
  { key: "CPF-MA", value: "CPF-MA" },
  { key: "SRS", value: "SRS" },
  { key: "CPF-MA & Cash", value: "CPF-MA & Cash" },
];

const policyIllustrationdata = [
  { key: "Additional Cover", value: "Additional Cover" },
  { key: "Booster", value: "Booster" },
  { key: "Guaranteed Benefit", value: "Guaranteed Benefit" },
  { key: "Guaranteed Death Benefit", value: "Guaranteed Death Benefit" },
  { key: "Life Benefit Multiplier", value: "Life Benefit Multiplier" },
  { key: "Minimum Protection Amount", value: "Minimum Protection Amount" },
  { key: "Minimum Protection Cover", value: "Minimum Protection Cover" },
  { key: "Minimum Protection Value", value: "Minimum Protection Value" },
  { key: "Additional Sum Insured", value: "Additional Sum Insured" },
];

const typeData = [
  { key: "$", value: "$" },
  { key: "%", value: "%" },
];

var productRecommendation = {}; //Single recommended product
//var prodRecommendationList = []; //List of recommended products (Moved into State)

var prodRider = {}; //List of recommended products

export default function ProductInsurance() {
  //REFERENCES TO CHILD COMPONENT
  const multiplierRef = useRef(null);
  const riderRef = useRef(null);

  const applicantCount = useSelector((state) => state.app.applicantCount);
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const applicants = useSelector((state) => state.personalInformation);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);
  //var productRecommendation = useSelector((state) => state.productRecommendation[currApplicant]);

  const protection = useSelector((state) => state.objectives.protection);
  const wealthaccumulation = useSelector((state) => state.objectives.wealthaccumulation);
  const investmentRisk = useSelector((state) => state.investmentRisk);

  const [proposer, setProposer] = useState("");
  const [lifeAssured, setLifeAssured] = useState("");
  const [needs, setNeeds] = useState("");

  //PRODUCT DETAILS
  const [companyId, setCompanyId] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [productId, setProductId] = useState("");
  const [sumAssured, setSumAssured] = useState("");
  const [paymentFrequency, setPaymentFrequency] = useState("");
  const [premium, setPremium] = useState("");
  const [premiumTerm, setPremiumTerm] = useState("");
  const [premiumTermError, setPremiumTermError] = useState("");
  const [policyTerm, setPolicyTerm] = useState("");
  const [policyTermError, setPolicyTermError] = useState("");
  const [settlementMode, setSettlementMode] = useState("");

  //PRODUCT RIDER
  const [riderOnly, setRiderOnly] = useState(false);

  //TIME HORIZON DECLARED
  const [timeHorizonDeclared, setTimeHorizonDeclared] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [companiesData, setCompaniesData] = useState([]);
  var [productCategoryData, setProductCategoryData] = useState([]);
  var [productData, setProductData] = useState([]);
  var [riderData, setRiderData] = useState([]);

  const dispatch = useDispatch();

  //SELECTED PRODUCT TABLE ROW
  const [selectedRow, setSelectedRow] = useState(0);
  const [prodRecommendationList, setProdRecommendationList] = useState([]);

  //SELECTED RIDER TABLE ROW
  const [selectedRiderRow, setSelectedRiderRow] = useState(0);
  const [prodRiderList, setProdRiderList] = useState([]);

  var proposersData = [];
  var lifeAssuredData = [];
  var needsData = [];

  /**==============================
   * EFFECTS - FETCH COMPANY
   ==============================*/
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        if (companiesData.length === 0) {
          const response = await fetch("https://fp-api.3nity.tech/insurance_company");

          if (!response.ok) throw new Error("Failed to fetch insurance company");
          const data = await response.json();
          const companyList = data.insurance_company_list;
          companyList.map((company) => {
            companiesMap.set(company.id, company.name);
            companiesData.push({ key: company.id, value: company.name });
          });

          setCompaniesData(companiesData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, []);

  /**==============================
   * EFFECTS - PRODUCT CATEGORY
   ==============================*/
  useEffect(() => {
    const fetchProductCategory = async () => {
      productCategoryData = [];
      productCategoryMap.clear();

      try {
        const response = await fetch(`https://fp-api.3nity.tech/product_categories/${companyId}`);
        if (!response.ok) throw new Error("Failed to product category");
        const data = await response.json();
        const productCategoryList = data.productcat_list;
        productCategoryList.map((productCategory) => {
          productCategoryMap.set(productCategory.id, productCategory.name);
          productCategoryData.push({ key: productCategory.prodcat_id, value: productCategory.name });
        });

        setProductCategoryData(productCategoryData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductCategory();
  }, [companyId]);

  /**==============================
   * EFFECTS - PRODUCT NAME
   ==============================*/
  useEffect(() => {
    const fetchProduct = async () => {
      productData = [];
      productMap.clear();

      try {
        const response = await fetch(`https://fp-api.3nity.tech/product/${productCategoryId}`);
        if (!response.ok) throw new Error("Failed to product category");
        const data = await response.json();
        const productList = data.product_list;

        productList.map((product) => {
          productMap.set(product.id, product.name);
          productData.push({ key: product.id, value: product.name });
        });

        setProductData(productData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productCategoryId]);

  /**==============================
   * EFFECTS - RIDER
   ==============================*/
  useEffect(() => {
    const fetchRider = async () => {
      riderData = [];
      riderMap.clear();

      try {
        const response = await fetch(`https://fp-api.3nity.tech/insurance_rider/${companyId}`);
        if (!response.ok) throw new Error("Failed to fetch rider list");
        const data = await response.json();
        const riderList = data.insurancerider_list;
        riderList.map((rider) => {
          riderMap.set(rider.id, rider.name);
          riderData.push({ key: rider.id, value: rider.name });
        });

        setRiderData(riderData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRider();
  }, [companyId]);

  initComponent();

  function initComponent() {
    initProposer();
    initLifeAssured();
    initNeeds();
  }

  function initProposer() {
    proposersData = [];

    //INITIALIZE PROPOSER
    // if (applicantCount === "ONE") {
    proposersData.push({ key: personalInfo.fullName, value: personalInfo.fullName });
    // } else if (applicantCount === "TWO") {
    //   for (const [key, value] of Object.entries(applicants)) {
    //     let applicantKey = `${key}`;
    //     let applicantValue = applicants[applicantKey];
    //     proposersData.push({ key: applicantValue.fullName, value: applicantValue.fullName });
    //   }
    // }
  }

  function initNeeds() {
    //INITIALIZE NEEDS - PROTECTION
    needsData.splice(0, needsData.length);

    for (let key in protection) {
      let priority = protection[key].priority;
      if (priority === "MEDIUM" || priority === "HIGH") {
        // needsData.push({ key: `${key}`, value: `${key}` });
        needsData.push({ key: `${key}`, value: "[" + priority + "]: " + needsMap.get(`${key}`) });
      }
    }

    for (let key in wealthaccumulation) {
      let priority = wealthaccumulation[key].priority;
      if (priority === "MEDIUM" || priority === "HIGH")
        needsData.push({ key: `${key}`, value: "[" + priority + "]: " + needsMap.get(`${key}`) });
    }

    // for (const [key, value] of Object.entries(protection)) {
    //   let priority = `${value.priority}`;
    //   if (priority === "MEDIUM" || priority === "HIGH")
    //     needsData.push({ key: `${key}`, value: "[" + priority + "]: " + needsMap.get(`${key}`) });
    // }

    //INITIALIZE NEEDS - WEALTH ACCUMUCATION
    // for (const [key, value] of Object.entries(wealthaccumulation)) {
    //   let priority = `${value.priority}`;
    //   if (priority === "MEDIUM" || priority === "HIGH")
    //     needsData.push({ key: `${key}`, value: "[" + priority + "]: " + needsMap.get(`${key}`) });
    // }
  }

  function initLifeAssured() {
    //INITIALIZE PROPOSER
    if (applicantCount === "ONE") {
      lifeAssuredData.push({ key: personalInfo.fullName, value: personalInfo.fullName });
    } else if (applicantCount === "TWO") {
      for (const [key, value] of Object.entries(applicants)) {
        let applicantKey = `${key}`;
        let applicantValue = applicants[applicantKey];
        lifeAssuredData.push({ key: applicantValue.fullName, value: applicantValue.fullName });
      }
    }
  }

  // function updateRedux(fieldName, fieldValue, reducer) {
  //   const action = {
  //     type: fieldName,
  //     payload: fieldValue,
  //   };
  //   dispatch(reducer(action));
  // }

  /**==============================
   * ACTION HANDLER
   ==============================*/
  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    if (e instanceof PointerEvent) {
      id = object.props.id;

      if (id === "proposer") {
        productRecommendation.proposer = e.target.value;
        setProposer(e.target.value);
        //actionType = "UPDATE_PROPOSER";
      }

      if (id === "lifeAssured") {
        productRecommendation.lifeAssured = e.target.value;
        setLifeAssured(e.target.value);
        //actionType = "UPDATE_LIST_ASSURED";
      }

      if (id === "needs") {
        productRecommendation.needs = e.target.value;
        setNeeds(e.target.value);
        //actionType = "UPDATE_NEEDS";
      }

      if (id === "company") {
        productRecommendation.companyId = e.target.value;
        productRecommendation.companyName = companiesMap.get(e.target.value);

        setCompanyId(e.target.value);
        //actionType = "UPDATE_COMPANY";
      }

      if (id === "productCategory") {
        productRecommendation.productCategoryId = e.target.value;
        productRecommendation.productCategory = productCategoryMap.get(e.target.value);

        setProductCategoryId(e.target.value);
        //actionType = "UPDATE_PRODUCT_CATEGORY";
      }

      if (id === "productName") {
        productRecommendation.productId = e.target.value;
        productRecommendation.productName = productMap.get(e.target.value);

        setProductId(e.target.value);
        //actionType = "UPDATE_PRODUCT";
      }

      if (id === "paymentFrequency") {
        productRecommendation.paymentFrequency = e.target.value;

        setPaymentFrequency(e.target.value);
        //actionType = "UPDATE_PAYMENT_FREQUENCY";
      }

      if (id === "settlementMode") {
        productRecommendation.settlementMode = e.target.value;
        setSettlementMode(e.target.value);
        //actionType = "UPDATE_PAYMENT_SETTLEMENT_MODE";
      }
    } else {
      id = e.target.id;

      let input_data = {
        event: e,
        fieldName: "",
        fieldVariable: "",
        isMandatory: false,
      };

      if (id === "riderOnly") {
        productRecommendation.riderOnly = e.target.checked;
        setRiderOnly(e.target.checked);
        //actionType = "UPDATE_RIDER_ONLY";
      }

      if (id === "sumAssured") {
        input_data.fieldName = "Sum assured";
        input_data.fieldVariable = sumAssured;
        const result = validateCurrencyField3(input_data);
        if (result.errorMessage === "") {
          setSumAssured(result.value);

          productRecommendation.sumAssured = result.value;
          //actionType = "UPDATE_SUM_ASSURED";
        }
      }

      if (id === "premium") {
        input_data.fieldName = "Premium";
        input_data.fieldVariable = premium;
        const result = validateCurrencyField3(input_data);
        if (result.errorMessage === "") {
          setPremium(result.value);

          productRecommendation.premium = result.value;
          //actionType = "UPDATE_SUM_PREMIUM";
        }
      }

      if (id === "premiumTerm" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setPremiumTermError("Premium term is required.");
        } else if (e.target.value.length > 3) {
          setPremiumTermError("Premium term exceeded 3 characters");
        } else setPremiumTermError("");

        setPremiumTerm(e.target.value);
        productRecommendation.premiumTerm = e.target.value;
        //actionType = "UPDATE_SUM_PREMIUM_TERM";
      }

      if (id === "policyTerm" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setPolicyTermError("Policy term is required.");
        } else if (e.target.value.length > 3) {
          setPolicyTermError("Policy term exceeded 3 characters");
        } else setPolicyTermError("");

        setPolicyTerm(e.target.value);
        productRecommendation.policyTerm = e.target.value;
        //actionType = "UPDATE_SUM_POLICY_TERM";
      }

      if (id === "timeHorizonDeclared") {
        productRecommendation.timeHorizonDeclared = e.target.checked;
        setTimeHorizonDeclared(e.target.checked);
      }
    }

    // if (actionType !== "") {
    //   payload = createPayload(currApplicant, productRecommendation);
    //   updateAction = createReduxAction(actionType, payload);
    //   dispatch(updateProductRecommendation(updateAction));
    // }
  }

  /**==================================================
   * VALIDATION - ENSURE MANDATORY FIELDS ARE POPULATED
   ==================================================*/
  function validate() {
    //VALIDATE THIS COMPONEN
    if (proposer.length <= 0) return false;
    if (lifeAssured.length <= 0) return false;
    if (needs.length <= 0) return false;

    if (companyId.length <= 0) return false;
    if (productCategoryId.length <= 0) return false;
    if (productId.length <= 0) return false;

    if (riderOnly === false) {
      if (sumAssured.length <= 0) return false;
      if (paymentFrequency.length <= 0) return false;
      if (premium.length <= 0) return false;
      if (premiumTerm.length <= 0) return false;
      if (policyTerm.length <= 0) return false;
    }

    if (settlementMode.length <= 0) return false;

    //VALIDATE PRODUCT MULTIPLIER
    if (multiplierRef.current) {
      if (multiplierRef.current.validate() === false) return false;
    }

    return true;
  }

  /**==================================================
   * ADD PRODUCT INTO PRODUCT RECOMMENDATION LIST
   ==================================================*/
  function handleAddProduct() {
    let actionType = "UPDATE_PRODCT_RECOMMENDATION_LIST";
    let updateAction = null;
    let payload = null;

    //WHEN VALIDATION FAILS - DON'T PROCEED
    if (!validate()) return;

    //Add Current Product Recommendation into List
    productRecommendation.investmentRisk = investmentRisk.riskType;
    //productRecommendation.prodRiderList = prodRiderList;

    if (riderRef.current) productRecommendation.prodRiderList = riderRef.current.getRiderList();

    //Insert into list
    const newRows = [...prodRecommendationList, productRecommendation];
    setProdRecommendationList(newRows);
    console.log(
      "ProductIndurace | handleAddProduct | prodRecommendationList.length = " + prodRecommendationList.length
    );

    // if (actionType !== "") {
    //   payload = createPayload(currApplicant, prodRecommendationList);
    //   updateAction = createReduxAction(actionType, payload);
    //   dispatch(updateProductRecommendation(updateAction));
    // }

    //Clear product recommendation object
    productRecommendation = {};
    // setProdRiderList([]);
    if (riderRef.current) riderRef.current.clearRiderList();

    //Clear UI components
    clearScreen();
  }

  /**==================================================
   * SELECTED ROW - FUTURE ENHANCEMENT TO HANDLE EDIT
   ==================================================*/
  function handleSelectRow() {
    //console.log("handleSelectRow");
  }

  /**==================================================
   * REMOVE A TARGETED PRODUCT FROM PRODUCT LIST
   ==================================================*/
  function removeProduct(index) {
    var tempRow = prodRecommendationList.slice();
    tempRow.splice(index, 1);
    setProdRecommendationList(tempRow);
  }

  function clearScreen() {
    //Reset Product Components
    setProposer("");
    setLifeAssured("");
    setNeeds("");

    setCompanyId("");
    setProductCategoryId("");
    setProductId("");
    setSumAssured("");
    setPaymentFrequency("");
    setPremium("");
    setPremiumTerm("");
    setPolicyTerm("");
    setSettlementMode("");

    //Reset Multiplier Component
    if (multiplierRef.current) multiplierRef.current.clearScreen();
    if (riderRef.current) riderRef.current.clearScreen();
  }

  return (
    <>
      {/*==============================
        DEPENDANT - TABLE LIST
        ==============================*/}
      {prodRecommendationList.length >= 1 && (
        <>
          <Accordion>
            <AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel1-content" id="panel1-header">
              <Typography variant="body2" align="justify" sx={{ textDecoration: "underline", fontWeight: 1000 }}>
                Insurance Product Recommendation
              </Typography>
            </AccordionSummary>

            <Grid>
              {prodRecommendationList.map((row, index) => (
                <>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ArrowDropDownIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Typography variant="body2" align="justify" color="blue" sx={{ fontWeight: 1000 }}>
                        RECOMMENDATION {index + 1}: Needs: [{row.needs}] Company: [{row.companyName}]
                      </Typography>
                    </AccordionSummary>

                    <Grid size={12}>
                      <TableContainer component={Paper}>
                        <TableHead>
                          <TableRow>
                            <TableCell align="left">Needs</TableCell>
                            <TableCell align="left">Life Assured</TableCell>
                            <TableCell align="left">Insurance Product</TableCell>

                            <TableCell align="left">Settlement Mode</TableCell>
                            <TableCell align="left">Risk Profile</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          <TableRow
                            key={row.index}
                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                            selected={selectedRow === index + 1}
                            onClick={() => handleSelectRow(index + 1, row)}
                          >
                            <TableCell align="left">{row.needs}</TableCell>
                            <TableCell align="left">{row.lifeAssured}</TableCell>
                            <TableCell align="left">{row.companyName}</TableCell>
                            <TableCell align="left">{row.settlementMode}</TableCell>
                            <TableCell align="left">{row.investmentRisk}</TableCell>

                            <TableCell align="left">
                              <Button variant="text" onClick={() => removeProduct(index)} sx={{ color: "red" }}>
                                X
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </TableContainer>

                      {/*PRODUCT MULTIPLIER */}
                      {row.addProductMultiplier && (
                        <>
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<ArrowDropDownIcon />}
                              aria-controls="panel1-content"
                              id="panel1-header"
                            >
                              <Typography variant="body2" align="justify" sx={{ fontWeight: 1000 }}>
                                PRODUCT MULIPLIER
                              </Typography>
                            </AccordionSummary>

                            <TableContainer component={Paper}>
                              <TableHead>
                                <TableRow>
                                  <TableCell align="left">Additional Cover</TableCell>
                                  <TableCell align="left">Premium</TableCell>
                                  <TableCell align="left">Premium Term</TableCell>
                                  <TableCell align="left">Policy Term</TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                <TableCell align="left">{row.prodMultiplierSumAssured}</TableCell>
                                <TableCell align="left">{row.prodMultiplierPremium}</TableCell>
                                <TableCell align="left">{row.prodMultiplierPremiumTerm}</TableCell>
                                <TableCell align="left">{row.prodMultiplierPolicyTerm}</TableCell>
                              </TableBody>
                            </TableContainer>
                          </Accordion>
                        </>
                      )}

                      {/*RIDER*/}
                      {row.prodRiderList.length >= 1 && (
                        <>
                          {row.prodRiderList.map((rider, index) => (
                            <>
                              <Accordion>
                                <AccordionSummary
                                  expandIcon={<ArrowDropDownIcon />}
                                  aria-controls="panel1-content"
                                  id="panel1-header"
                                >
                                  <Typography variant="body2" align="justify" sx={{ fontWeight: 1000 }}>
                                    RIDER {index + 1}
                                  </Typography>
                                </AccordionSummary>

                                <TableContainer component={Paper}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell align="left">Rider Name</TableCell>
                                      <TableCell align="left">Sum Assured</TableCell>
                                      <TableCell align="left">Premium</TableCell>
                                      <TableCell align="left">Premium Term</TableCell>
                                      <TableCell align="left">Policy Term</TableCell>
                                    </TableRow>
                                  </TableHead>

                                  <TableBody>
                                    <TableCell align="left">{rider.riderName}</TableCell>
                                    <TableCell align="left">{rider.riderSumAssured}</TableCell>
                                    <TableCell align="left">{rider.riderPremium}</TableCell>
                                    <TableCell align="left">{rider.riderPremiumTerm}</TableCell>
                                    <TableCell align="left">{rider.riderCoverageTerm}</TableCell>
                                  </TableBody>
                                </TableContainer>

                                {rider.addProductMultiplier && (
                                  <>
                                    <Accordion>
                                      <AccordionSummary
                                        expandIcon={<ArrowDropDownIcon />}
                                        aria-controls="panel1-content"
                                        id="panel1-header"
                                      >
                                        <Typography variant="body2" align="justify" sx={{ fontWeight: 1000 }}>
                                          RIDER {index + 1} - MULTIPLER
                                        </Typography>
                                      </AccordionSummary>

                                      <TableContainer component={Paper}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell align="left">Additional Cover</TableCell>
                                            <TableCell align="left">Premium</TableCell>
                                            <TableCell align="left">Premium Term</TableCell>
                                            <TableCell align="left">Policy Term</TableCell>
                                          </TableRow>
                                        </TableHead>

                                        <TableBody>
                                          <TableCell align="left">{rider.prodMultiplierSumAssured}</TableCell>
                                          <TableCell align="left">{rider.prodMultiplierPremium}</TableCell>
                                          <TableCell align="left">{rider.prodMultiplierPremiumTerm}</TableCell>
                                          <TableCell align="left">{rider.prodMultiplierPolicyTerm}</TableCell>
                                        </TableBody>
                                      </TableContainer>
                                    </Accordion>
                                  </>
                                )}
                              </Accordion>
                            </>
                          ))}
                        </>
                      )}
                    </Grid>
                  </Accordion>
                </>
              ))}
            </Grid>
          </Accordion>
        </>
      )}

      <Grid container spacing={2}>
        <GridSectionHeader sectionName="Product Recommendation - Insurance" />

        {/* Proposer */}
        <GridDropdown
          id="proposer"
          key="proposer"
          columnSize="6"
          label="Proposer"
          currState={proposer}
          onChange={handleChange}
          data={proposersData}
        />

        {/* Life Assured */}
        <GridDropdown
          id="lifeAssured"
          key="lifeAssured"
          columnSize="6"
          label="Life Assured"
          currState={lifeAssured}
          onChange={handleChange}
          data={lifeAssuredData}
        />

        {/* Needs */}
        <GridDropdown
          id="needs"
          key="needs"
          columnSize="12"
          label="Needs"
          currState={needs}
          onChange={handleChange}
          data={needsData}
        />

        {/*==============================
        PRODUCT DETAILS
        ==============================*/}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ backgroundColor: "lightgray" }}>
            Product Details
          </Typography>
        </Grid>
        {/* Company */}
        <GridDropdown
          id="company"
          key="company"
          columnSize="6"
          label="Company"
          currState={companyId}
          onChange={handleChange}
          data={companiesData}
        />
        {/* Clients's Risk Profile */}
        <GridTextfield
          id="clientRiskProfile"
          key="clientRiskProfile"
          columnSize="6"
          label="Client Risk Profile"
          currState={investmentRisk.riskType}
          disabled={true}
        />
        {/* Product Category */}
        <GridDropdown
          id="productCategory"
          key="productCategory"
          columnSize="12"
          label="Product Category"
          currState={productCategoryId}
          onChange={handleChange}
          data={productCategoryData}
        />
        {/* Product Name */}
        <GridDropdown
          id="productName"
          key="productName"
          columnSize="12"
          label="Product Name"
          currState={productId}
          onChange={handleChange}
          data={productData}
        />

        {!riderOnly && (
          <>
            {/* SUM ASSURED */}
            <GridTextfield
              id="sumAssured"
              key="sumAssured"
              columnSize="6"
              label="Sum Assured"
              currState={sumAssured}
              onChange={handleChange}
            />

            {/* PAYMENT FREQUENCY */}
            <GridDropdown
              id="paymentFrequency"
              key="paymentFrequency"
              columnSize="6"
              label="Payment Frequency"
              defaultValue=""
              value=""
              currState={paymentFrequency}
              onChange={handleChange}
              data={paymentFrequencyData}
            />

            {/* PREMIUM */}
            <GridTextfield
              id="premium"
              key="premium"
              columnSize="6"
              label="Premium"
              currState={premium}
              onChange={handleChange}
            />

            {/* PREMIUM TERM*/}
            <GridTextfield
              id="premiumTerm"
              key="premiumTerm"
              columnSize="6"
              label="Premium Term"
              currState={premiumTerm}
              onChange={handleChange}
              error={premiumTermError}
              helperText={premiumTermError}
            />

            {/* POLICY TERM */}
            <GridTextfield
              id="policyTerm"
              key="policyTerm"
              columnSize="6"
              label="Policy Term"
              currState={policyTerm}
              onChange={handleChange}
              error={policyTermError}
              helperText={policyTermError}
            />
          </>
        )}

        {/* SETTLEMENT MODE */}
        <GridDropdown
          id="settlementMode"
          key="settlementMode"
          columnSize="6"
          label="Settlement Mode"
          currState={settlementMode}
          onChange={handleChange}
          data={settlementModeData}
        />

        <GridCheckbox
          id="riderOnly"
          columnSize="12"
          label="Add rider only."
          value="riderOnly"
          disabled={false}
          onChange={handleChange}
          checked={riderOnly}
        />

        {/*==============================
         MULTIPLIER / ADDITIONAL COVER DETAILS
        ==============================*/}
        {!riderOnly && (
          <Multiplier
            title="Product Multiplier"
            productRecommendation={productRecommendation}
            childRef={multiplierRef}
          />
        )}

        {/*==============================
         RIDER DETAILS
        ==============================*/}
        <Rider companyId={companyId} productRecommendation={productRecommendation} childRef={riderRef} />

        {/*==============================
         COMMANDS
        ==============================*/}
        <Grid size={12}>
          <Stack spacing={2} direction="row" justifyContent="center">
            <Button
              variant="contained"
              sx={{ width: "140px" }}
              disabled={false}
              // onClick={handleCancel}
            >
              Clear Redux
            </Button>

            <Button variant="contained" sx={{ width: "140px" }} disabled={false} onClick={handleAddProduct}>
              Add Product
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
